import type { Dispatch, ReactNode, RefObject, SetStateAction } from 'react';
import { cloneElement, isValidElement } from 'react';
import { Drawer } from 'vaul';
import { MOBILE_ANALYSIS_SHEET_STACKING, MOBILE_SHEET_BOTTOM_OFFSET } from './analysisMobileSheetConstants';

export type AnalysisMobileBottomSheetProps = {
    setMeasureEl: Dispatch<SetStateAction<HTMLDivElement | null>>;
    measureEl: HTMLDivElement | null;
    measureReady: boolean;
    drawerLayoutKey: number;
    mobileMinHeightPx: string;
    mobilePeekHeightPx: string;
    mobileExpandedHeightPx: string;
    mobileAnalysisSnapPoint: number | string | null;
    setMobileAnalysisSnapPoint: Dispatch<SetStateAction<number | string | null>>;
    peekMeasureRef: RefObject<HTMLDivElement | null>;
    peekHandleOnlyRef: RefObject<HTMLDivElement | null>;
    mobileSheetScrollRef: RefObject<HTMLDivElement | null>;
    isMobileAnalysisSheetExpanded: boolean;
    searchAreaTitle: string;
    analysisToolbarMiddle: ReactNode;
    analysisToolbarDock?: ReactNode;
    analysisPeekSummary: ReactNode;
    /** null のときスクロール枠なし */
    analysisMobileExpandedScrollContent: ReactNode | null;
};

/**
 * 分析ページ・モバイル: Vaul ボトムシート（3 スナップ + ツールバー差し込み）
 */
export function AnalysisMobileBottomSheet({
    setMeasureEl,
    measureEl,
    measureReady,
    drawerLayoutKey,
    mobileMinHeightPx,
    mobilePeekHeightPx,
    mobileExpandedHeightPx,
    mobileAnalysisSnapPoint,
    setMobileAnalysisSnapPoint,
    peekMeasureRef,
    peekHandleOnlyRef,
    mobileSheetScrollRef,
    isMobileAnalysisSheetExpanded,
    searchAreaTitle,
    analysisToolbarMiddle,
    analysisToolbarDock,
    analysisPeekSummary,
    analysisMobileExpandedScrollContent,
}: AnalysisMobileBottomSheetProps) {
    return (
        <>
            <div
                ref={setMeasureEl}
                className={`pointer-events-none fixed top-0 right-0 left-0 ${MOBILE_ANALYSIS_SHEET_STACKING} overflow-visible`}
                style={{ bottom: MOBILE_SHEET_BOTTOM_OFFSET }}
            />
            {measureReady && (
                <Drawer.Root
                    key={drawerLayoutKey}
                    open
                    dismissible={false}
                    modal={false}
                    handleOnly
                    container={measureEl}
                    snapPoints={[mobileMinHeightPx, mobilePeekHeightPx, mobileExpandedHeightPx]}
                    fadeFromIndex={2}
                    snapToSequentialPoint
                    activeSnapPoint={mobileAnalysisSnapPoint}
                    setActiveSnapPoint={setMobileAnalysisSnapPoint}
                    shouldScaleBackground={false}
                >
                    <Drawer.Portal container={measureEl}>
                        <Drawer.Content
                            className="pointer-events-auto absolute inset-x-0 bottom-0 flex h-full max-w-none flex-col rounded-t-xl border border-gray-200 bg-white px-1.5 shadow-[0_-4px_16px_rgba(0,0,0,0.12)] outline-none sm:px-3"
                            aria-describedby={undefined}
                        >
                            <Drawer.Title className="sr-only">{searchAreaTitle}</Drawer.Title>
                            <div
                                ref={peekMeasureRef}
                                className="shrink-0 flex flex-col border-b border-gray-100"
                            >
                                <div
                                    ref={peekHandleOnlyRef}
                                    id="analysis-mobile-panel-toggle"
                                    className="flex w-full shrink-0 justify-center pt-2.5 pb-1"
                                >
                                    <Drawer.Handle className="h-1 w-9 shrink-0 rounded-full bg-gray-300" />
                                </div>
                                <div
                                    className={
                                        isMobileAnalysisSheetExpanded
                                            ? 'mt-3 flex flex-col gap-5 px-2 pb-2 sm:px-3'
                                            : 'mt-3 flex flex-col gap-4 px-2 pb-0.5 sm:px-3'
                                    }
                                >
                                    {analysisToolbarMiddle != null ? (
                                        <div className="w-full px-1">
                                            {isValidElement(analysisToolbarMiddle)
                                                ? cloneElement(analysisToolbarMiddle, {
                                                      omitTutorialDateNavId: true,
                                                      hideDateNavigation: true,
                                                      compactBucketTabs: !isMobileAnalysisSheetExpanded,
                                                  } as {
                                                      omitTutorialDateNavId?: boolean;
                                                      hideDateNavigation?: boolean;
                                                      compactBucketTabs?: boolean;
                                                  })
                                                : analysisToolbarMiddle}
                                        </div>
                                    ) : null}
                                    {analysisToolbarDock != null ? (
                                        <div data-analysis-dock-interactive className="w-full">
                                            {isValidElement(analysisToolbarDock)
                                                ? cloneElement(analysisToolbarDock, {
                                                      dockComfortableHeight: isMobileAnalysisSheetExpanded,
                                                  } as {
                                                      dockComfortableHeight?: boolean;
                                                  })
                                                : analysisToolbarDock}
                                        </div>
                                    ) : analysisPeekSummary ? (
                                        <div className="py-1 text-center text-xs text-gray-600">
                                            {analysisPeekSummary}
                                        </div>
                                    ) : null}
                                </div>
                            </div>
                            {analysisMobileExpandedScrollContent != null ? (
                                <div
                                    ref={mobileSheetScrollRef}
                                    id="analysis-mobile-panel-scroll"
                                    className="min-h-0 flex-1 overflow-y-auto px-4 pb-3 pt-1"
                                >
                                    {analysisMobileExpandedScrollContent}
                                </div>
                            ) : null}
                        </Drawer.Content>
                    </Drawer.Portal>
                </Drawer.Root>
            )}
        </>
    );
}
